import { useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { useNotification } from '../context/NotificationContext';

export const useInvites = () => {
  const { user } = useAuth();
  const { setTeams, teams } = useWorkspace();
  const { showNotification } = useNotification();

  // Function to check for invites that can be called manually
  const checkInvites = useCallback(async (force = false) => {
    if (!user) {
      return;
    }

    // Check if we've recently failed to check invites for this user
    const failedCheckKey = `invite_check_failed_${user.id}`;
    const lastFailedCheck = localStorage.getItem(failedCheckKey);
    
    if (!force && lastFailedCheck) {
      const failedTime = parseInt(lastFailedCheck, 10);
      const now = Date.now();
      
      // If we've failed within the last hour, don't try again
      if (now - failedTime < 60 * 60 * 1000) {
        return;
      }
      
      // Clear the failed flag if it's been more than an hour
      localStorage.removeItem(failedCheckKey);
    }

    try {
      // Get user's profile
      const { data: profile, error: profileError } = await supabase
        .from('Profiles')
        .select('id, email')
        .eq('user_id', user.id)
        .single();

      if (profileError) throw profileError;
      if (!profile) return;

      // Check for invites by email
      const { data: invites, error: invitesError } = await supabase
        .from('UserInvites')
        .select('*')
        .eq('email', profile.email);

      if (invitesError) throw invitesError;
      if (!invites || invites.length === 0) return;
      
      // Process each invite
      for (const invite of invites) {
        try {
          // Check if team exists using RPC function
          const { data: teamExists, error: checkError } = await supabase
            .rpc('check_team_exists', { team_id: invite.team_id });
            
          if (checkError) throw checkError;
          
          // If team doesn't exist, skip this invite
          if (!teamExists) continue;
          
          // Check if user already has access to this team
          const teamInContext = teams?.find(t => t.id === invite.team_id);
          
          if (!teamInContext) {
            // Add user to team using RPC function
            const { error: addError } = await supabase
              .rpc('add_user_to_team', {
                team_id: invite.team_id,
                user_id: user.id
              });

            if (addError) throw addError;
            
            // Get team name if possible
            let teamName = "a team";
            const { data: teamData } = await supabase
              .from('Teams')
              .select('name')
              .eq('id', invite.team_id)
              .single();
              
            if (teamData) {
              teamName = teamData.name;
            }
            
            // Show notification
            showNotification(`You joined ${teamName}`);
          }
          
          // Delete the invite regardless of whether we added the user
          // (if they're already in the team, we don't need the invite)
          await supabase
            .from('UserInvites')
            .delete()
            .eq('id', invite.id);
            
          // Refresh teams list
          const { data: updatedTeams, error: teamsError } = await supabase
            .from('Teams')
            .select('*')
            .contains('users', [user.id]);

          if (!teamsError && updatedTeams) {
            setTeams(updatedTeams);
          }
        } catch (error) {
          // console.error('Error processing invite:', error);
        }
      }
    } catch (error) {
      // console.error('Error checking invites:', error);
      
      // Mark this check as failed
      if (user) {
        localStorage.setItem(`invite_check_failed_${user.id}`, Date.now().toString());
      }
    }
  }, [user, setTeams, showNotification, teams]);

  // Set up a listener for the custom event to force check invites
  useEffect(() => {
    const handleForceCheckInvites = () => {
      checkInvites(true);
    };

    window.addEventListener('force-check-invites', handleForceCheckInvites);
    
    return () => {
      window.removeEventListener('force-check-invites', handleForceCheckInvites);
    };
  }, [checkInvites]);

  // Check invites once when the component mounts and user is available
  useEffect(() => {
    if (user) {
      checkInvites();
    }
  }, [user, checkInvites]);

  return { checkInvites };
}; 